/**
 * AP-INV-01: New Invoice Form
 * Route: /portal/invoices/new?poId=... (portal-protected)
 *
 * Step 1 — Select an ISSUED PO
 * Step 2 — Fetch invoiceable scope; enter per-line invoice quantities
 * Step 3 — Enter header details (invoice number, dates, tax number, notes)
 * Step 4 — Review totals (server-authoritative preview) → Save Draft or Submit
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, AlertCircle, ChevronRight, FileText, Send, Save } from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(minor, currency = "INR") {
  if (minor == null || minor === "") return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}

function fmtQty(qty) {
  if (qty === "" || qty == null) return "";
  const n = parseFloat(qty);
  return isNaN(n) ? qty : n % 1 === 0 ? String(Math.round(n)) : n.toFixed(3).replace(/\.?0+$/, "");
}

/** Client-side financial preview — mirrors server arithmetic for UX only. */
function previewLine(qty, unitRateMinor, taxRateBps) {
  const q = parseFloat(qty);
  if (isNaN(q) || q <= 0) return { subtotal: 0, tax: 0, total: 0 };
  const subtotal = Math.floor(q * unitRateMinor);
  const tax = Math.floor((subtotal * taxRateBps) / 10000);
  return { subtotal, tax, total: subtotal + tax };
}

export default function SupplierNewInvoice() {
  const { portalUser } = usePortalUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPoId = searchParams.get("poId") || "";

  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState(preselectedPoId);
  const [scope, setScope] = useState(null);      // invoiceable scope response
  const [loadingScope, setLoadingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");

  // Per-line state
  const [lineQtys, setLineQtys] = useState({});   // po_line_id → qty string
  const [lineEnabled, setLineEnabled] = useState({});  // po_line_id → bool

  // Header state
  const [supplierInvNum, setSupplierInvNum] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load ISSUED POs for selector
  useEffect(() => {
    axios.get(`${API}/api/portal/po`, { params: { status: "ISSUED", page_size: 50 }, withCredentials: true })
      .then((r) => setPos(r.data.items || []))
      .catch(() => toast.error("Failed to load purchase orders"));
  }, []);

  // Load invoiceable scope when PO is selected
  const loadScope = useCallback(async (poId) => {
    if (!poId) { setScope(null); setLineQtys({}); setLineEnabled({}); return; }
    setLoadingScope(true);
    setScopeError("");
    try {
      const res = await axios.get(`${API}/api/portal/invoices/invoiceable`, {
        params: { po_id: poId }, withCredentials: true,
      });
      setScope(res.data);
      const qtys = {}, enabled = {};
      (res.data.lines || []).forEach((l) => {
        qtys[l.po_line_id] = "";
        enabled[l.po_line_id] = parseFloat(l.remaining_invoiceable_quantity) > 0;
      });
      setLineQtys(qtys);
      setLineEnabled(enabled);
    } catch (err) {
      setScopeError(err.response?.data?.detail || "Failed to load invoiceable scope");
    } finally {
      setLoadingScope(false);
    }
  }, []);

  useEffect(() => { loadScope(selectedPoId); }, [selectedPoId, loadScope]);

  // Build invoice lines for submission
  function buildLines() {
    if (!scope) return [];
    return (scope.lines || [])
      .filter((l) => lineEnabled[l.po_line_id] && parseFloat(lineQtys[l.po_line_id]) > 0)
      .map((l) => ({ po_line_id: l.po_line_id, invoiced_quantity: lineQtys[l.po_line_id] }));
  }

  // Server-preview totals (client-side for UX — server is authoritative)
  const previewTotals = useCallback(() => {
    if (!scope) return { subtotal: 0, tax: 0, total: 0 };
    let st = 0, tx = 0;
    (scope.lines || []).forEach((l) => {
      if (!lineEnabled[l.po_line_id]) return;
      const qty = lineQtys[l.po_line_id];
      if (!qty || parseFloat(qty) <= 0) return;
      const p = previewLine(qty, l.unit_rate_minor, l.tax_rate_bps);
      st += p.subtotal; tx += p.tax;
    });
    return { subtotal: st, tax: tx, total: st + tx };
  }, [scope, lineQtys, lineEnabled]);

  async function handleSave(andSubmit = false) {
    setError("");
    const lines = buildLines();
    if (lines.length === 0) { setError("Add at least one line with a quantity greater than zero."); return; }
    if (!supplierInvNum.trim()) { setError("Supplier Invoice Number is required."); return; }
    if (!invoiceDate) { setError("Invoice Date is required."); return; }

    setSubmitting(true);
    try {
      const body = {
        po_id: selectedPoId,
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        supplier_invoice_number: supplierInvNum.trim(),
        supplier_tax_number: taxNumber || null,
        lines,
        notes: notes || null,
      };
      const res = await axios.post(`${API}/api/portal/invoices`, body, { withCredentials: true });
      const inv = res.data;

      if (andSubmit) {
        await axios.post(`${API}/api/portal/invoices/${inv.id}/submit`, {}, { withCredentials: true });
        toast.success("Invoice submitted successfully");
        navigate(`/portal/invoices/${inv.id}`);
      } else {
        toast.success("Draft invoice saved");
        navigate(`/portal/invoices/${inv.id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to save invoice";
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  const totals = previewTotals();
  const currency = scope?.currency_code || "INR";
  const hasInvoiceableLines = scope?.lines?.some((l) => parseFloat(l.remaining_invoiceable_quantity) > 0);

  return (
    <div data-testid="supplier-new-invoice-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate("/portal/invoices")} data-testid="back-to-invoices-btn"
          style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#8A7A6A", cursor: "pointer", fontSize: "13px" }}>
          <ArrowLeft size={16} /> My Invoices
        </button>
        <ChevronRight size={14} style={{ color: "#555" }} />
        <span style={{ fontSize: "13px", color: "#C9A15F", fontWeight: 500 }}>New Invoice</span>
      </nav>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(22px,4vw,28px)", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>
          Create Invoice
        </h2>
        <p style={{ fontSize: "13px", color: "#6B5B4A", margin: "0 0 32px" }}>
          Submit a digital invoice against accepted purchase order scope
        </p>

        {error && (
          <div data-testid="invoice-error-banner" style={{ display: "flex", alignItems: "flex-start", gap: "10px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px" }}>
            <AlertCircle size={16} style={{ color: "#DC2626", flexShrink: 0, marginTop: "2px" }} />
            <span style={{ fontSize: "13px", color: "#991B1B" }}>{error}</span>
          </div>
        )}

        {/* Step 1: PO Selection */}
        <section style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", padding: "24px", marginBottom: "20px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1A1A1A", margin: "0 0 16px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Purchase Order
          </h3>
          <div>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "#6B5B4A", display: "block", marginBottom: "6px" }}>
              Select Issued PO *
            </label>
            <select
              data-testid="po-selector"
              value={selectedPoId}
              onChange={(e) => { setSelectedPoId(e.target.value); setError(""); }}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #DDD6CE", borderRadius: "8px", fontSize: "13px", background: "#FFF", color: "#1A1A1A", cursor: "pointer" }}
            >
              <option value="">— Select a Purchase Order —</option>
              {pos.map((po) => (
                <option key={po.id} value={po.id}>
                  {`${po.po_number || po.id}${po.event_name ? ` — ${po.event_name}` : ""}`}
                </option>
              ))}
            </select>
          </div>

          {loadingScope && (
            <p style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "12px" }}>Loading invoiceable scope…</p>
          )}
          {scopeError && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "12px", color: "#DC2626", fontSize: "13px" }}>
              <AlertCircle size={14} /> {scopeError}
            </div>
          )}
          {scope && !loadingScope && !hasInvoiceableLines && (
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "8px", padding: "12px", marginTop: "12px" }}>
              <AlertCircle size={14} style={{ color: "#92400E", flexShrink: 0, marginTop: "2px" }} />
              <span style={{ fontSize: "13px", color: "#92400E" }}>
                This PO has no remaining invoiceable quantity. All accepted scope has already been invoiced.
              </span>
            </div>
          )}
        </section>

        {/* Step 2: Line Quantities */}
        {scope && !loadingScope && (
          <section style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", padding: "24px", marginBottom: "20px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1A1A1A", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Invoice Lines
            </h3>
            <p style={{ fontSize: "12px", color: "#9CA3AF", margin: "0 0 20px" }}>
              Enter quantities to invoice. You can partially invoice accepted scope.
            </p>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #F5F0EB" }}>
                    {["Description", "Unit", "Accepted", "Previously Invoiced", "Remaining", "Invoice Qty", "Preview Total"].map((h, i) => (
                      <th key={i} style={{ padding: "8px 12px", textAlign: i >= 5 ? "right" : "left", fontSize: "11px", fontWeight: 700, color: "#6B5B4A", letterSpacing: "0.4px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(scope.lines || []).map((line) => {
                    const remaining = parseFloat(line.remaining_invoiceable_quantity) || 0;
                    const isInvoiceable = remaining > 0;
                    const qty = lineQtys[line.po_line_id] || "";
                    const preview = previewLine(qty, line.unit_rate_minor, line.tax_rate_bps);
                    return (
                      <tr key={line.po_line_id} data-testid={`line-row-${line.po_line_id}`}
                        style={{ borderBottom: "1px solid #F5F0EB", opacity: isInvoiceable ? 1 : 0.45 }}>
                        <td style={{ padding: "12px 12px", fontSize: "13px", color: "#1A1A1A", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {line.description}
                        </td>
                        <td style={{ padding: "12px 12px", fontSize: "12px", color: "#6B5B4A" }}>{line.unit}</td>
                        <td style={{ padding: "12px 12px", fontSize: "12px", color: "#1A1A1A", textAlign: "right" }}>{fmtQty(line.net_accepted_quantity)}</td>
                        <td style={{ padding: "12px 12px", fontSize: "12px", color: "#6B5B4A", textAlign: "right" }}>{fmtQty(line.total_invoiced_quantity)}</td>
                        <td style={{ padding: "12px 12px", fontSize: "12px", textAlign: "right", fontWeight: 600, color: remaining > 0 ? "#166534" : "#9CA3AF" }}>
                          {fmtQty(line.remaining_invoiceable_quantity)}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "right" }}>
                          <input
                            data-testid={`line-qty-input-${line.po_line_id}`}
                            type="number"
                            min="0"
                            step="0.001"
                            max={remaining}
                            value={qty}
                            disabled={!isInvoiceable}
                            placeholder={isInvoiceable ? `Max ${fmtQty(line.remaining_invoiceable_quantity)}` : "Not invoiceable"}
                            onChange={(e) => setLineQtys((prev) => ({ ...prev, [line.po_line_id]: e.target.value }))}
                            style={{ width: "110px", padding: "7px 10px", border: "1px solid #DDD6CE", borderRadius: "6px", fontSize: "13px", textAlign: "right", background: isInvoiceable ? "#FFF" : "#F9F9F9", color: isInvoiceable ? "#1A1A1A" : "#9CA3AF" }}
                          />
                        </td>
                        <td style={{ padding: "12px 12px", textAlign: "right", fontSize: "13px", fontWeight: 600, color: preview.total > 0 ? "#1A1A1A" : "#C9B8A6" }}>
                          {preview.total > 0 ? fmt(preview.total, currency) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Step 3: Invoice Header Details */}
        {scope && !loadingScope && (
          <section style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", padding: "24px", marginBottom: "20px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1A1A1A", margin: "0 0 20px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Invoice Details
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
              {[
                { label: "Supplier Invoice Number *", id: "sup-inv-num", value: supplierInvNum, setter: setSupplierInvNum, type: "text", placeholder: "e.g. INV-2025-001", testId: "supplier-invoice-number-input" },
                { label: "Invoice Date *", id: "inv-date", value: invoiceDate, setter: setInvoiceDate, type: "date", testId: "invoice-date-input" },
                { label: "Due Date", id: "due-date", value: dueDate, setter: setDueDate, type: "date", testId: "due-date-input" },
                { label: "GSTIN / Tax Number", id: "tax-num", value: taxNumber, setter: setTaxNumber, type: "text", placeholder: "e.g. 27AAAAA0000A1Z5", testId: "tax-number-input" },
              ].map(({ label, id, value, setter, type, placeholder, testId }) => (
                <div key={id}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#6B5B4A", display: "block", marginBottom: "6px" }}>{label}</label>
                  <input data-testid={testId} type={type} value={value} placeholder={placeholder}
                    onChange={(e) => setter(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #DDD6CE", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#6B5B4A", display: "block", marginBottom: "6px" }}>Notes</label>
                <textarea data-testid="invoice-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes for AP team…" rows={2}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #DDD6CE", borderRadius: "8px", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }} />
              </div>
            </div>
          </section>
        )}

        {/* Step 4: Review + Submit */}
        {scope && !loadingScope && (
          <section style={{ background: "#1A1A1A", border: "1px solid #2D2D2D", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#F5EFE6", margin: "0 0 20px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Invoice Summary
            </h3>
            <div style={{ display: "flex", gap: "32px", marginBottom: "24px", flexWrap: "wrap" }}>
              {[
                { label: "Subtotal", value: totals.subtotal },
                { label: "Tax", value: totals.tax },
                { label: "Total", value: totals.total, big: true },
              ].map(({ label, value, big }) => (
                <div key={label}>
                  <p style={{ fontSize: "11px", color: "#8A7A6A", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
                  <p style={{ fontSize: big ? "22px" : "16px", fontWeight: big ? 700 : 600, color: big ? "#C9A15F" : "#F5EFE6", margin: 0 }}>
                    {fmt(value, currency)}
                  </p>
                </div>
              ))}
            </div>
            <p style={{ fontSize: "11px", color: "#6B5B4A", margin: "0 0 24px" }}>
              Preview amounts are indicative. Server recalculates totals on submission using canonical PO rates.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                data-testid="save-draft-btn"
                disabled={submitting || !hasInvoiceableLines}
                onClick={() => handleSave(false)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "transparent", border: "1px solid #C9A15F", color: "#C9A15F", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.7 : 1 }}
              >
                <Save size={15} />
                Save Draft
              </button>
              <button
                data-testid="save-submit-btn"
                disabled={submitting || !hasInvoiceableLines}
                onClick={() => handleSave(true)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 24px", background: "#C9A15F", border: "none", color: "#1A1A1A", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.7 : 1 }}
              >
                <Send size={15} />
                {submitting ? "Submitting…" : "Save & Submit Invoice"}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
